/* ── ResearchBriefView ──────────────────────────────────────────
   Renders a structured research brief response: table of contents,
   section-by-section analysis with per-section citations,
   executive summary, progress indicator, and consolidated sources.
   
   This is a SEPARATE view that coexists with the chat — it does
   NOT modify Chat.tsx, Message.tsx, or existing streaming logic.
   ────────────────────────────────────────────────────────────── */

import { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { BriefSection, BriefProgress, BriefMeta, Citation, BriefSectionStatus } from '../types';

/* ── Sub-components ─────────────────────────────────────────── */

/** Status icon for a section */
function SectionStatusIcon({ status }: { status: BriefSectionStatus }) {
  switch (status) {
    case 'queued':
      return <span className="rb-status-icon rb-status-icon--queued" aria-label="Queued">○</span>;
    case 'searching':
      return <span className="rb-status-icon rb-status-icon--searching" aria-label="Searching">🔍</span>;
    case 'writing':
      return <span className="rb-status-icon rb-status-icon--writing" aria-label="Writing">✏️</span>;
    case 'complete':
      return <span className="rb-status-icon rb-status-icon--complete" aria-label="Complete">✓</span>;
    case 'error':
      return <span className="rb-status-icon rb-status-icon--error" aria-label="Error">⚠️</span>;
    default:
      return <span className="rb-status-icon">○</span>;
  }
}

/** Progress bar component */
function ProgressBar({ percent, label }: { percent: number; label: string }) {
  return (
    <div className="rb-progress" role="progressbar" aria-valuenow={percent} aria-valuemin={0} aria-valuemax={100} aria-label={label}>
      <div className="rb-progress-bar">
        <div className="rb-progress-fill" style={{ width: `${Math.min(100, percent)}%` }} />
      </div>
      <span className="rb-progress-label">{label}</span>
    </div>
  );
}

/** Phase labels */
const PHASE_LABELS: Record<string, string> = {
  decomposing: 'Analysing query…',
  researching: 'Researching sections…',
  synthesising: 'Finalising brief…',
  complete: 'Complete',
};

/* ── Progress View ──────────────────────────────────────────── */

function ResearchBriefProgress({ progress }: { progress: BriefProgress }) {
  const phaseLabel = PHASE_LABELS[progress.phase] ?? 'Processing…';
  const completedCount = progress.sections.filter(s => s.status === 'complete').length;
  const totalCount = progress.sections.length;

  return (
    <div className="rb-progress-view" aria-live="polite">
      <div className="rb-progress-header">
        <span className="rb-progress-icon">📋</span>
        <span className="rb-progress-title">Research Brief</span>
        {totalCount > 0 && (
          <span className="rb-progress-count">
            {completedCount}/{totalCount} sections
          </span>
        )}
      </div>

      <ProgressBar percent={progress.overallPercent} label={phaseLabel} />

      {progress.statusText && (
        <div className="rb-status-ticker" aria-live="polite">
          {progress.statusText}
        </div>
      )}

      {progress.sections.length > 0 && (
        <div className="rb-section-plan">
          {progress.sections.map((section, idx) => (
            <div key={section.id} className={`rb-section-plan-item rb-section-plan-item--${section.status}`}>
              <span className="rb-section-plan-num">{'①②③④⑤⑥⑦⑧⑨⑩'.charAt(idx) || String(idx + 1)}</span>
              <span className="rb-section-plan-title">{section.title}</span>
              <SectionStatusIcon status={section.status} />
            </div>
          ))}
        </div>
      )}

      {progress.phase === 'decomposing' && progress.sections.length === 0 && (
        <div className="rb-decomposing">
          Breaking down your query into sections…
          <span className="rb-dots" aria-hidden="true">
            <span>●</span><span>●</span><span>●</span>
          </span>
        </div>
      )}
    </div>
  );
}

/* ── Section Component ──────────────────────────────────────── */

interface SectionProps {
  section: BriefSection;
  sectionNumber: number;
}

function BriefSectionView({ section, sectionNumber }: SectionProps) {
  const [showCitations, setShowCitations] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);
  const contentId = `rb-section-body-${section.id}`;

  const toggleCollapse = useCallback(() => {
    if (section.isStreaming) return;
    setCollapsed(v => !v);
  }, [section.isStreaming]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggleCollapse();
    }
  }, [toggleCollapse]);

  return (
    <section
      ref={sectionRef}
      className={`rb-section${section.isStreaming ? ' rb-section--streaming' : ''}${collapsed ? ' rb-section--collapsed' : ''}`}
      id={`rb-section-${section.id}`}
      aria-labelledby={`rb-section-heading-${section.id}`}
    >
      <div
        className={`rb-section-header${!section.isStreaming ? ' rb-section-header--clickable' : ''}`}
        role="button"
        tabIndex={section.isStreaming ? -1 : 0}
        aria-expanded={!collapsed}
        aria-controls={contentId}
        onClick={toggleCollapse}
        onKeyDown={handleKeyDown}
      >
        <h3 id={`rb-section-heading-${section.id}`} className="rb-section-heading">
          {!section.isStreaming && (
            <span className="rb-section-chevron" aria-hidden="true">{collapsed ? '▸' : '▾'}</span>
          )}
          <span className="rb-section-num">§{sectionNumber}</span>
          {section.title}
        </h3>
        {section.citations.length > 0 && (
          <button
            className="rb-section-citations-toggle"
            onClick={(e) => { e.stopPropagation(); setShowCitations(v => !v); }}
            aria-expanded={showCitations}
            aria-label={`${section.citations.length} sources for section ${sectionNumber}`}
            type="button"
          >
            {section.citations.length} 📎
          </button>
        )}
      </div>

      <div
        className={`rb-section-content${collapsed && !section.isStreaming ? ' rb-section-content--hidden' : ''}`}
        id={contentId}
      >
        {section.content ? (
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{section.content}</ReactMarkdown>
        ) : section.isStreaming ? (
          <span className="rb-streaming-cursor" aria-hidden="true" />
        ) : null}
      </div>

      {/* Per-section citation list — hidden when section is collapsed */}
      {showCitations && !collapsed && section.citations.length > 0 && (
        <div className="rb-section-sources">
          <div className="rb-section-sources-header">Section Sources</div>
          {section.citations.map((c, i) => (
            <div key={c.id} className="rb-section-source-item">
              <span className="rb-source-num">[{i + 1}]</span>
              <span className="rb-source-ref">
                {c.standard} {c.clause ? `§${c.clause}` : ''}
                {c.text && <span className="rb-source-title"> — {c.text.slice(0, 100)}{c.text.length > 100 ? '…' : ''}</span>}
              </span>
            </div>
          ))}
        </div>
      )}

      <div className="rb-section-divider" />
    </section>
  );
}

/* ── Table of Contents ──────────────────────────────────────── */

function TableOfContents({ sections }: { sections: BriefSection[] }) {
  const scrollToSection = useCallback((id: number) => {
    const el = document.getElementById(`rb-section-${id}`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  if (sections.length === 0) return null;

  return (
    <nav className="rb-toc" aria-label="Table of contents">
      <div className="rb-toc-title">Contents</div>
      {sections.map((s, idx) => (
        <button
          key={s.id}
          className={`rb-toc-item${s.isComplete ? ' rb-toc-item--complete' : ''}`}
          onClick={() => scrollToSection(s.id)}
          type="button"
        >
          <span className="rb-toc-num">{idx + 1}.</span>
          <span className="rb-toc-label">{s.title}</span>
          <span className="rb-toc-dots" aria-hidden="true" />
          <span className="rb-toc-citations">{s.citations.length}</span>
        </button>
      ))}
    </nav>
  );
}

/* ── Citation Appendix ──────────────────────────────────────── */

function CitationAppendix({ sections }: { sections: BriefSection[] }) {
  // Deduplicate citations across all sections, maintaining order
  const allCitations = useMemo(() => {
    const seen = new Set<string>();
    const result: (Citation & { sectionNums: number[] })[] = [];
    sections.forEach((sec, secIdx) => {
      sec.citations.forEach(c => {
        const key = `${c.standard}:${c.clause}`;
        if (!seen.has(key)) {
          seen.add(key);
          result.push({ ...c, sectionNums: [secIdx + 1] });
        } else {
          const existing = result.find(r => `${r.standard}:${r.clause}` === key);
          if (existing && !existing.sectionNums.includes(secIdx + 1)) {
            existing.sectionNums.push(secIdx + 1);
          }
        }
      });
    });
    return result;
  }, [sections]);

  if (allCitations.length === 0) return null;

  return (
    <div className="rb-appendix">
      <h3 className="rb-appendix-title">Sources</h3>
      <div className="rb-appendix-list">
        {allCitations.map((c, i) => (
          <div key={`${c.standard}-${c.clause}-${i}`} className="rb-appendix-item">
            <span className="rb-appendix-num">[{i + 1}]</span>
            <div className="rb-appendix-ref">
              <span className="rb-appendix-standard">{c.standard}</span>
              {c.clause && <span className="rb-appendix-clause"> §{c.clause}</span>}
              {c.text && <div className="rb-appendix-excerpt">{c.text.slice(0, 200)}{c.text.length > 200 ? '…' : ''}</div>}
              <span className="rb-appendix-sections">Used in §{c.sectionNums.join(', §')}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── Main ResearchBriefView ─────────────────────────────────── */

interface ResearchBriefViewProps {
  sections: BriefSection[];
  executiveSummary: string;
  progress: BriefProgress | null;
  meta: BriefMeta | null;
  error: string | null;
  onStop?: () => void;
  onClose?: () => void;
}

export default function ResearchBriefView({
  sections,
  executiveSummary,
  progress,
  meta,
  error,
  onStop,
  onClose,
}: ResearchBriefViewProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  // Auto-scroll as new content arrives
  const lastContentLenRef = useRef(0);
  useEffect(() => {
    const totalLen = sections.reduce((acc, s) => acc + s.content.length, 0) + executiveSummary.length;
    if (totalLen > lastContentLenRef.current) {
      lastContentLenRef.current = totalLen;
      // Scroll to bottom of container
      if (containerRef.current) {
        const scrollParent = containerRef.current.closest('.messages');
        if (scrollParent) {
          scrollParent.scrollTop = scrollParent.scrollHeight;
        }
      }
    }
  }, [sections, executiveSummary]);

  const isGenerating = progress !== null && progress.phase !== 'complete';
  const completedSections = sections.filter(s => s.isComplete || s.content.length > 0);
  const totalCitations = sections.reduce((acc, s) => acc + s.citations.length, 0);

  return (
    <div className="rb-container" ref={containerRef}>
      {/* Brief Header */}
      <div className="rb-header">
        <div className="rb-header-icon">📋</div>
        <div className="rb-header-text">
          <h2 className="rb-title">Research Brief</h2>
          {meta && (
            <div className="rb-meta">
              {meta.sectionCount} sections · {totalCitations} citations
              {meta.generatedAt && ` · ${meta.generatedAt}`}
            </div>
          )}
        </div>
        {isGenerating && onStop && (
          <button className="rb-stop-btn" onClick={onStop} aria-label="Stop generating" type="button">
            Stop
          </button>
        )}
        {onClose && (
          <button className="rb-close-btn" onClick={onClose} aria-label="Close research brief" type="button">
            ✕ Close
          </button>
        )}
      </div>

      {/* Progress indicator — shown while generating */}
      {isGenerating && progress && (
        <ResearchBriefProgress progress={progress} />
      )}

      {/* Error state */}
      {error && (
        <div className="rb-error" role="alert">
          <span className="rb-error-icon">⚠️</span>
          <span>{error}</span>
        </div>
      )}

      {/* Executive Summary */}
      {executiveSummary && (
        <div className="rb-executive-summary">
          <h3 className="rb-executive-summary-title">Executive Summary</h3>
          <div className="rb-executive-summary-content">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{executiveSummary}</ReactMarkdown>
          </div>
        </div>
      )}

      {/* Table of Contents — shown once we have sections */}
      {completedSections.length > 1 && (
        <TableOfContents sections={completedSections} />
      )}

      {/* Section-by-section rendering */}
      {completedSections.map((section, idx) => (
        <BriefSectionView
          key={section.id}
          section={section}
          sectionNumber={idx + 1}
        />
      ))}

      {/* Consolidated Sources */}
      {!isGenerating && completedSections.length > 0 && (
        <CitationAppendix sections={completedSections} />
      )}

      {/* Disclaimer footer — shown when complete */}
      {!isGenerating && completedSections.length > 0 && (
        <div className="rb-disclaimer">
          <strong>Research aid only.</strong> This document does not constitute legal or professional compliance advice. Always verify against official standard publications.
        </div>
      )}
    </div>
  );
}
